'use client';
import { sendOnbEvent } from '@/components/newRelic';
import { RootState } from '@/redux/store';
import { Button, Stack, Typography } from '@mui/material';
import { rejection } from '@repo/onb-api';
import { MaterialIcon, SafeSpacePadder } from '@repo/ui';
import { formatter } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

const isOnbCommerce = process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-comercios';
const isOnbCuotas = process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-cuotas';

function renderOnbCommerceMessage(rejectionReason?: string) {
  switch (rejectionReason) {
    case 'IDENTITY_VERIFICATION_ATTEMPTS_LIMIT':
    case 'IDENTITY_VERIFICATION_PROCESS_FAILED':
      return (
        <Typography color="secondary">
          No pudimos validar tu identidad. Podés intentarlo en otro momento y seguro te vamos a ayudar.
        </Typography>
      );
    case 'CUSTOMER_RISK_EVALUATION_REJECTED':
    case 'ORDER_EXCEEDS_APPROVED_AMOUNT':
      return (
        <Typography variant="h1" color="secondary">
          En este momento no podemos continuar con tu solicitud.
        </Typography>
      );
    case 'TIME_OUT':
      return <Typography color="secondary">Tu tiempo de sesión se agotó, por favor volvé a intentarlo.</Typography>;
    case 'INSUFFICIENT_FUNDS':
      return (
        <Typography color="secondary">
          Revisá con el emisor de tu tarjeta si los límites de operación diarios son correctos y si tu tarjeta tiene los
          fondos necesarios para completar la transacción.
        </Typography>
      );
    case 'PAYMENT_GATEWAY_ERROR':
      return (
        <Typography color="secondary">
          El pago no ha sido autorizado. Por favor contactesé con el emisor de su tarjeta para solicitar autorización.
        </Typography>
      );
    case 'INVALID_MERCHANT_IDENTIFICATION':
      return (
        <Stack gap={2}>
          <Typography color="secondary">
            Tu CUIT ya ha sido utilizado. Por favor, comunicate con nuestro equipo de soporte para más información.
          </Typography>
        </Stack>
      );
    default:
      return (
        <Typography variant="h1" color="secondary">
          En este momento no podemos continuar con tu solicitud.
        </Typography>
      );
  }
}

function renderDefaultMessage(rejectionReason?: string, policyMaximumAmount?: number) {
  switch (rejectionReason) {
    case 'IDENTITY_VERIFICATION_ATTEMPTS_LIMIT':
    case 'IDENTITY_VERIFICATION_PROCESS_FAILED':
      return (
        <Typography color="secondary">
          No pudimos validar tu identidad. Podés intentarlo en otro momento y seguro te vamos a ayudar.
        </Typography>
      );
    case 'CUSTOMER_RISK_EVALUATION_REJECTED':
    case 'ORDER_EXCEEDS_APPROVED_AMOUNT':
      if (isOnbCuotas && policyMaximumAmount) {
        return (
          <>
            <Typography variant="h1" color="primary">
              Podés comprar hasta {formatter(policyMaximumAmount, 'currency')}
            </Typography>
            <Typography color="secondary">y tu carrito excede ese importe.</Typography>
          </>
        );
      }
      return (
        <Typography variant="h1" color="secondary">
          No tenemos una oferta disponible.
        </Typography>
      );
    case 'TIME_OUT':
      return <Typography color="secondary">Tu tiempo de sesión se agotó, por favor volvé a intentarlo.</Typography>;
    case 'INSUFFICIENT_FUNDS':
      return (
        <Typography color="secondary">
          Revisá con el emisor de tu tarjeta si los límites de operación diarios son correctos y si tu tarjeta tiene los
          fondos necesarios para completar la transacción.
        </Typography>
      );
    case 'PAYMENT_GATEWAY_ERROR':
      return (
        <Typography color="secondary">
          El pago no ha sido autorizado. Por favor contactesé con el emisor de su tarjeta para solicitar autorización.
        </Typography>
      );
    case 'INVALID_MERCHANT_IDENTIFICATION':
      return (
        <Typography color="secondary">
          Tu CUIT ya ha sido utilizado. Por favor, comunicate con nuestro equipo de soporte para más información.
        </Typography>
      );
    default:
      return (
        <Typography variant="h1" color="secondary">
          No tenemos una oferta disponible.
        </Typography>
      );
  }
}

export default function Rejection({ stepdata: stepData }: { stepdata?: rejection['variables'] }) {
  const { firstName } = useSelector((state: RootState) => state.flowdata.variables) || {};
  const policyMaximumAmount = stepData?.policyMaximumAmount;
  const rejectionReason = stepData?.rejectionReason;
  const router = useRouter();
  const internal = useSelector((state: RootState) => state.additionalData.internal);
  const whatsappNumber = isOnbCommerce ? '5491157494024' : '5491131878888';
  useEffect(() => {
    sendOnbEvent({ type: 'interaction', subtype: 'rejected', data: { rejectionReason } });
  }, []);

  const rejectionMessage = isOnbCommerce
    ? renderOnbCommerceMessage(rejectionReason)
    : renderDefaultMessage(rejectionReason, policyMaximumAmount);

  const onbCommerceRejectionCodes: Record<string, string> = {
    INVALID_MERCHANT_IDENTIFICATION: 'Código 1',
    WATCHLIST_REPET_MATCH: 'Código 2',
    SMS_OTP_VALIDATION_ATTEMPTS_LIMIT: 'Código 3',
    MERCHANT_RISK_EVALUATION_REJECTED: 'Código 4',
    MERCHANT_RISK_EVALUATION_INVALID_LEGAL_FORM: 'Código 5',
    IDENTITY_VERIFICATION_ATTEMPTS_LIMIT: 'Código 6',
    TIME_OUT: 'Código 7',
  };

  const codeExists = rejectionReason && Object.keys(onbCommerceRejectionCodes).includes(rejectionReason);

  return (
    <SafeSpacePadder
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'center',
        gap: 4,
        height: '100%',
        width: '100%',
        paddingBottom: { xs: 4, md: 8 },
      }}
    >
      <Stack alignItems="center" gap={4} height={'100%'} justifyContent={'center'}>
        <Stack gap={0}>
          <MaterialIcon icon="support_agent" size={64} color="secondary" />
          {isOnbCommerce ? (
            <Typography color="secondary">
              {rejectionReason && codeExists ? onbCommerceRejectionCodes[rejectionReason] : ''}
            </Typography>
          ) : null}
        </Stack>
        <Stack gap={2}>
          <Typography variant="h1">Lo sentimos {formatter(firstName, 'capitalize')}</Typography>
          {rejectionMessage}
          {rejectionReason !== 'PAYMENT_GATEWAY_ERROR' && (
            <Typography variant="body1" sx={{ mt: 2 }} color="secondary">
              Tenés dudas?
              <br />
              Escribinos a nuestro{' '}
              <a style={{ textDecoration: 'underline' }} href={`https://wa.me/${whatsappNumber}`} target="_blank">
                WhatsApp
              </a>
            </Typography>
          )}
        </Stack>
      </Stack>
      {!internal &&
        (isOnbCuotas ? (
          <Button fullWidth variant="contained" id="button-cancel" onClick={() => router.push('/back_to_store')}>
            Volver a la tienda
          </Button>
        ) : (
          <Button fullWidth variant="contained" id="button-cancel" onClick={() => router.push('/')}>
            Volver al Inicio
          </Button>
        ))}
    </SafeSpacePadder>
  );
}
